/**
 * L'API BDESE, côté navigateur.
 *
 * Aucune règle métier ici : le calcul, la déduction du seuil, le régime de
 * confidentialité et le rendu adapté au destinataire sont tous des réponses du
 * serveur. L'écran affiche, il ne recalcule pas — un chiffre recalculé côté
 * navigateur serait un second endroit où se tromper, et le seul que personne ne
 * relit.
 */
import axios from 'axios';
import { APP_CONFIG } from '@/constants/app';
import { tokenManager } from '@/utils/token-manager';
import type {
  EtatModalitesBdese,
  FixerModalitesBdese,
  EtatInformationsBdese,
  InformationBdese,
  RapportConsulte,
  RapportResume,
  Referentiel,
  SaisieIndicateur,
} from '../types';

const api = axios.create({
  baseURL: APP_CONFIG.api.baseUrl,
  headers: { 'Content-Type': 'application/json' },
});
tokenManager.setupInterceptors(api);

/** L'API enveloppe ses réponses dans `{ statusCode, data, meta }`. */
function extraire<T>(reponse: { data: unknown }): T {
  const corps = reponse.data as { data?: T };
  return (corps?.data ?? (corps as unknown)) as T;
}

export const bdeseService = {
  /** Les exercices d'un dossier, du plus récent au plus ancien. */
  async exercices(folderUuid: string): Promise<RapportResume[]> {
    const liste = extraire<RapportResume[]>(
      await api.get(`/bdese/folders/${folderUuid}/reports`)
    );
    return Array.isArray(liste) ? liste : [];
  },

  /**
   * Ouvre un exercice. La catégorie de seuil n'est PAS passée : elle est
   * déduite de l'effectif par le serveur. La saisir depuis l'écran laisserait
   * choisir le référentiel applicable, donc l'étendue de l'obligation.
   */
  async ouvrirExercice(
    folderUuid: string,
    annee: number
  ): Promise<RapportResume> {
    return extraire<RapportResume>(
      await api.post('/bdese/reports', { folderUuid, annee })
    );
  },

  async consulter(reportUuid: string): Promise<RapportConsulte> {
    return extraire<RapportConsulte>(
      await api.get(`/bdese/reports/${reportUuid}`)
    );
  },

  /** Ce que la loi attend sur cet exercice. Déduit par le serveur. */
  async referentiel(reportUuid: string): Promise<Referentiel> {
    const r = extraire<Referentiel>(
      await api.get(`/bdese/reports/${reportUuid}/referentiel`)
    );
    return { ...r, rubriques: Array.isArray(r?.rubriques) ? r.rubriques : [] };
  },

  /** Renseigne une valeur que la paie ne fournit pas. */
  async saisir(reportUuid: string, saisie: SaisieIndicateur): Promise<unknown> {
    return extraire(
      await api.put(`/bdese/reports/${reportUuid}/indicateurs`, saisie)
    );
  },

  async calculer(reportUuid: string): Promise<unknown> {
    return extraire(await api.post(`/bdese/reports/${reportUuid}/calcul`, {}));
  },

  /** Modalités fixées par l'employeur au titre de R2312-12. */
  async lireModalites(folderUuid: string): Promise<EtatModalitesBdese> {
    return extraire<EtatModalitesBdese>(
      await api.get(`/bdese/folders/${folderUuid}/modalites`)
    );
  },

  /** La même route métier que l'API expose : aucune règle n'est dupliquée ici. */
  async fixerModalites(
    folderUuid: string,
    modalites: FixerModalitesBdese
  ): Promise<EtatModalitesBdese> {
    return extraire<EtatModalitesBdese>(
      await api.put(`/bdese/folders/${folderUuid}/modalites`, modalites)
    );
  },

  /** Suivi d'acheminement réservé au cabinet et à l'employeur. */
  async lireInformations(folderUuid: string): Promise<EtatInformationsBdese> {
    const resultat = extraire<EtatInformationsBdese | InformationBdese[]>(
      await api.get(`/bdese/folders/${folderUuid}/informations`)
    );
    // Compatibilité non permissive avec l'API historique : elle rendait la
    // liste seule, donc aucune reprise manuelle n'était autorisée à l'écran.
    return Array.isArray(resultat)
      ? { informations: resultat, repriseAutorisee: false }
      : {
          informations: Array.isArray(resultat?.informations)
            ? resultat.informations
            : [],
          repriseAutorisee: resultat?.repriseAutorisee === true,
        };
  },

  async reprendreInformations(
    folderUuid: string
  ): Promise<{ reprises: number }> {
    return extraire<{ reprises: number }>(
      await api.post(`/bdese/folders/${folderUuid}/informations/reprendre`, {})
    );
  },

  /**
   * Télécharge l'export. `responseType: 'blob'` est indispensable : sans lui,
   * axios décode le binaire en texte et le PDF arrive corrompu — un fichier
   * `.pdf` qui n'en est pas, exactement ce que le serveur refuse de produire.
   */
  async telecharger(
    reportUuid: string,
    format: 'pdf' | 'xlsx'
  ): Promise<{ blob: Blob; nom: string }> {
    const reponse = await api.get(`/bdese/reports/${reportUuid}/export`, {
      params: { format },
      responseType: 'blob',
    });
    const entete = String(reponse.headers?.['content-disposition'] ?? '');
    const trouve = /filename="?([^";]+)"?/.exec(entete);
    return {
      blob: reponse.data as Blob,
      nom: trouve?.[1] ?? `bdese.${format}`,
    };
  },
};
